import { apiAxios } from "./api-config/apiConfig";

/* =====================================================
   DASHBOARD TYPES
===================================================== */

export interface Category {
  id: string;
  name: string;
}

export interface Lesson {
  lessonId: string;
  categories: Category[] | null;
  lessonName: string;
  lessonCompleted: boolean | null;
  lessonButtonName: string;
  lessonType: "video" | "document" | "website";
}

export interface Activity {
  activityId: string;
  activityName: string;
  activityValue: string | string[] | null;
  activityOption: string[];
  activityDescription: string;
  activityGoalUnit: string;
  activityGoal: number;
  activityMappingId: string | null;
  activityLogoName: string;
}

export interface WeekReport {
  topic: string[];
  status: string;
  dueDate: string;
}

export interface Resource {
  categoryId: string;
  resourceId: string;
  resourceDesc: string;
  resourceName: string;
  resourceButtonName: string | null;
  resourceThumbnail: string;
}

export interface TotalWeekData {
  weekId: string;
  weekName: string;
  weekStatus: string;
}

export interface DashboardUserDetail {
  enrolledDate: string;
  firstName: string;
  userId: string;
}

export interface DeliveredMessage {
  createdAt: string;
  id: string;
  message: string;
}

export interface DashboardCategories {
  categoryId: string;
  categoryName: string;
  isActive: boolean;
  sortOrder: number;
}

export interface WeekData {
  weekId: string;
  weekName: string;
  totalLessons: string;
  completedLessons: string;
  lessonDetails: Lesson[] | null;
  resources: Resource[] | null;
  activities: Activity[] | null;
  weekReport: WeekReport[] | null;
  streakValue: number | null;
  totalWeeks: TotalWeekData[];
  userDetails: DashboardUserDetail[];
  deliveredMessage: DeliveredMessage[];
  categories: DashboardCategories[];
}

export interface DashboardResponse {
  status: boolean;
  data: WeekData[];
}

/* =====================================================
   DASHBOARD API
===================================================== */

export const getDashboardData = async (
  startDate: string,
  endDate: string,
  weekId?: string,
): Promise<DashboardResponse> => {
  const response = await apiAxios.get(`/user/dashboard`, {
    params: {
      startDate,
      endDate,
      ...(weekId && { weekId }),
    },
  });

  return response.data;
};

/* =====================================================
   WEEK QUESTIONS TYPES
===================================================== */

export interface QuestionOption {
  optionId: string;
  optionName: string;
  optionSortOrder: number;
}

export interface WeekQuestion {
  questionId: string;
  questionName: string;
  questionDesc: string;
  questionSortOrder: number;
  options: QuestionOption[];
}

export interface AnsweredWeekQuestion extends WeekQuestion {
  answer: string | null;
}

export interface WeekQuestionsResponse {
  status: boolean;
  data: WeekQuestion[];
}

/* =====================================================
   WEEK QUESTIONS API
===================================================== */

export const getWeekQuestions = async (
  weekId: string,
): Promise<WeekQuestionsResponse> => {
  const response = await apiAxios.get(`/week/${weekId}/questions`);

  return response.data;
};

/* =====================================================
   SUBMIT WEEK ANSWERS TYPES
===================================================== */

export interface SubmitWeekAnswerPayload {
  answer: AnsweredWeekQuestion[];
}

export interface SubmitWeekAnswerResponse {
  status: boolean;
  data: string;
}

/* =====================================================
   SUBMIT WEEK ANSWERS API
===================================================== */

export const submitWeekAnswers = async (
  weekId: string,
  payload: SubmitWeekAnswerPayload,
): Promise<SubmitWeekAnswerResponse> => {
  const response = await apiAxios.post(`/week/${weekId}/answer`, payload);

  return response.data;
};

/* =====================================================
   WEEK LIST
===================================================== */

export interface Week {
  id: string;
  name: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface WeekListResponse {
  status: boolean;
  data: Week[];
}

export const getWeekList = async (): Promise<WeekListResponse> => {
  const response = await apiAxios.get("/week/list");
  return response.data;
};
